import { Mui, MuiIcons } from '@osu/react-ui';
import { useState } from 'react';
import useRoleAuthentication from '../../../Common/hooks/useRoleAuthentication';
import { TOOLTIPS } from "../../../util/constants";
import { handleKeyPresses } from "./rechist-treegrid-accessibility";

// Sections a non-admin (3rd/4th reviewer) is allowed to see for the 1st/2nd reviews.
// Titles must match those produced by getUnifiedNotesSections in Applicant/transform.jsx.
const ATTRIBUTE_SECTION_TITLES = ["Competitive Attributes", "Non-Competitive Attributes"];

const styles = {
  screenReaderOnly: {
    left: "-9999px",
    position: "absolute !important",
  },
  removeLastChildBottomBorder: {
    "& td:first-of-type": { textAlign: "center" },
    "&:nth-last-of-type(1 of .hasBottomLine) td": { borderBottom: "none" },
    "& td": {
      paddingTop: "0.2rem",
      paddingBottom: "0.2rem",
    },
  },
  thrReview: {
    textAlign: "center",
    width: "5rem",
  },
  thrTime: { width: "13rem" },
  verticallyCenter: {
    display: "flex",
    alignItems: "center",
  },
  clickToExpandRow: { cursor: "pointer" },
  expandCollapse: { width: "52px" },
  expandableRow: {
    "& td": {
      borderBottom: "none",
      padding: "0",
    },
  },
  expandedRowDataWrapper: {
    backgroundColor: '#f2f2f2',
    padding: "24px 16px",
    "& span": {
      paddingTop: "4px",
      paddingBottom: "4px",
    },
  },
  expandableBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '3rem',
  },
  section: {
    display: "flex",
    flexDirection: 'column',
    width: 'auto',
    minWidth: '180px',
    maxWidth: '280px',
  },
  indent: { paddingLeft: "2rem" },
  invisibleButton: { visibility: "hidden" },
};

const HistoryTable = (props) => {
  const { recommendationHistory } = props?.studentData?.data ?? {};
  // console.log("recommendationHistory:", recommendationHistory)
  
  return recommendationHistory.length === 0 ? (
    'No recommendations have been submitted for this application.'
  ) : (
    <Mui.Table aria-label="Recommendation History" role="treegrid">
        <Mui.TableHead>
            <Mui.TableRow>
                <Mui.TableCell sx={styles.thrReview}>Review</Mui.TableCell>
                <Mui.TableCell sx={styles.thrTime}>Assign Time</Mui.TableCell>
                <Mui.TableCell sx={styles.thrTime}>Submit Time</Mui.TableCell>
                <Mui.TableCell>Name</Mui.TableCell>
                <Mui.TableCell>Recommendation</Mui.TableCell>
                <Mui.TableCell aria-label="Button to expand/collapse row" sx={styles.expandCollapse}></Mui.TableCell>
            </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
            {
                recommendationHistory.map((row, index, allRows) => {
                    return ( <HistoryReviewerDoubleRow key={index} row={row} index={index} allRows={allRows} /> );
                })
            }
        </Mui.TableBody>
    </Mui.Table>
  );
};

function HistoryReviewerDoubleRow({row, index, allRows}) {
    const [expanded, setExpanded] = useState(false);
    const authDetails = useRoleAuthentication();
    const isAdmin = authDetails.ADMIN;

    // 3rd/4th reviewers may expand another reviewer's row to see the Competitive/Non-Competitive
    // attributes of the 1st/2nd reviews. The API only returns those attributes to an eligible
    // (3rd/4th) viewer, so their mere presence on another reviewer's row authorizes the expand.
    const isOwnReview = row.reviewerEmployeeId && row.reviewerEmployeeId === authDetails.CURRENT_EMPLID;
    const hasViewableAttributes = row.comments?.some?.(section => ATTRIBUTE_SECTION_TITLES.includes(section.title) && section.hasNotes);
    const canExpand = isAdmin || (hasViewableAttributes && !isOwnReview);
    const attributesOnly = !isAdmin;

    function flipExpanded(event) { setExpanded(canExpand && !this); event.preventDefault(); event.stopPropagation(); }
    function getExpandedButton(expanded) { return expanded ? <MuiIcons.KeyboardArrowUp /> : <MuiIcons.KeyboardArrowDown /> }
    
    const parentRowSx = { ...styles.removeLastChildBottomBorder, ...(canExpand ? styles.clickToExpandRow : {}) };
    const buttonSx = canExpand ? {} : styles.invisibleButton;
    const keyPressHandler = handleKeyPresses.bind({ expanded, setExpanded });
    const colSpanCount = 5 + (canExpand ? 1 : 0);
    const showNotificationIcon = row.notificationCreated && (!row.notificationCleared);
    
    return (
        <>
            {/* Main row of reviewer recommendation details */}
            <Mui.TableRow data-rowhasfocusables={"true"} onKeyDown={keyPressHandler} className="hasBottomLine" sx={parentRowSx} onClick={flipExpanded.bind(expanded)} role="row" aria-level="1" aria-posinset={index} aria-setsize={allRows.length}>
                <Mui.TableCell role="gridcell" aria-label={`${expanded ? "Expanded" : "Collapsed"} ${row.stage}`} tabIndex="0">{row.stage}</Mui.TableCell>
                <Mui.TableCell role="gridcell" tabIndex="0">{row.assignTimeFormatted}</Mui.TableCell>
                <Mui.TableCell role="gridcell" tabIndex="0">{row.submitTimeFormatted}</Mui.TableCell>
                <Mui.TableCell role="gridcell" tabIndex="0"><div sx={styles.verticallyCenter}>{row.nameFormatted}{showNotificationIcon && ( TOOLTIPS('NOTIFY') )}</div></Mui.TableCell>
                <Mui.TableCell role="gridcell" tabIndex="0">{row.recommendationUnified}</Mui.TableCell>
                <Mui.TableCell role="gridcell">
                    <Mui.IconButton sx={buttonSx} onClick={flipExpanded.bind(expanded)} aria-label={expanded ? "Row Expanded" : "Row Collapsed"} size="small" tabIndex="0">
                        { getExpandedButton(expanded) }
                    </Mui.IconButton>
                </Mui.TableCell>
            </Mui.TableRow>
            
            {/* Expandable row of reviewer notes/details for admins */}
            { canExpand && (
                <Mui.TableRow data-rowhasfocusables={expanded} onKeyDown={keyPressHandler} sx={styles.expandableRow} role="row" aria-expanded={expanded.toString()} aria-level="2" aria-posinset="1" aria-setsize="1" tabIndex="-1">
                    <Mui.TableCell aria-label="Review Notes" colSpan={colSpanCount} role="gridcell" tabIndex="-1">
                        <Mui.Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Mui.Box sx={styles.expandedRowDataWrapper}>
                                <HistoryRow2ExpandableBox expanded={expanded} row={row} attributesOnly={attributesOnly} />
                            </Mui.Box>
                        </Mui.Collapse>
                    </Mui.TableCell>
                </Mui.TableRow>
            )}
        </>
    );
}

function HistoryRow2ExpandableBox({ expanded, row, attributesOnly }) {
    const isAppDecisionOverride = !attributesOnly && row.type === "ApplicationDecisionOverride" && row.adminOverrideComment?.length > 0;
    // Non-admin (3rd/4th reviewer) viewers only see attribute sections; admins see all sections.
    const sections = attributesOnly
        ? (row.comments?.filter?.(section => ATTRIBUTE_SECTION_TITLES.includes(section.title)) ?? [])
        : (row.comments ?? []);
    const commentsExist = isAppDecisionOverride || sections.some?.(section => section.hasNotes);

    if (!commentsExist       ) { return <span data-focusable={expanded} tabIndex="0">There are no associated notes with this review.</span>; }
    if (isAppDecisionOverride) { return <span data-focusable={expanded} tabIndex="0"><b>Administrative Recommendation Comments:</b> {row.adminOverrideComment}</span>; }

    return (
        <>
            <Mui.Typography sx={styles.screenReaderOnly} component="h3">Notes from {row.nameFormatted}</Mui.Typography>
            <Mui.Box sx={styles.expandableBox}>
                { sections.map((section, index) => ( <HistoryRowNotes key={index} expanded={expanded} index={index} section={section} /> )) }
            </Mui.Box>
        </>
    );
}

function HistoryRowNotes({ expanded, index, section }) {

    return (
        <Mui.Box aria-label={`${section.title} Section`} sx={styles.section} data-focusable={expanded} tabIndex="0">
            <Mui.Typography component="h4" variant="subtitle2">{section.title}</Mui.Typography>
            { section.notes?.map((note, index) => ( <span style={note.shouldIndent ? styles.indent : undefined} key={index}>{note.label}</span> )) }
        </Mui.Box>
    );
}

export default HistoryTable;